import {
  Building2,
  CalendarDays,
  CalendarRange,
  ClipboardList,
  Fingerprint,
  Flag,
  House,
  Laptop,
  ScrollText,
  TreePalm,
  UsersRound,
} from 'lucide-react';
import { AttendanceTodayHrWidget, TeamTodayWidget } from '@/features/attendance/dashboard-widgets';
import { AttendanceTodayWidget } from '@/features/attendance/today-card';
import { LeaveBalanceWidget } from '@/features/leave/dashboard-widget';
import type { AppModule } from './registry';

/**
 * Time & Leave module (M7, ARCHITECTURE §8.4, EXT-10): leave, attendance and WFH for every
 * signed-in user, HR pages by permission. The sidebar drops empty groups, so the group shows
 * only to those who may open one of its pages.
 */
export const timeModule: AppModule = {
  id: 'time',
  label: 'Time & Leave',
  nav: [
    {
      // My balances and requests (FR-LEAVE-8, PRD US-A4).
      id: 'my-leave',
      label: 'My leave',
      to: '/me/leave',
      icon: TreePalm,
      requiredPermissions: ['leave.request'],
    },
    {
      // Check in / out today and my WFH requests (FR-ATT-3, FR-WFH-1/3).
      id: 'my-attendance',
      label: 'My attendance',
      to: '/me/attendance',
      icon: House,
      requiredPermissions: ['wfh.request', 'attendance.checkin', 'attendance.regularise'],
    },
    {
      // My reporting tree's leave calendar (FR-LEAVE-8).
      id: 'team-leave',
      label: 'Team leave',
      to: '/team/leave',
      icon: CalendarRange,
      requiredPermissions: ['team.read'],
      requiresReports: true,
    },
    {
      // My reporting tree: today, the month grid and the WFH calendar (FR-ATT-11, FR-WFH-5).
      id: 'team-attendance',
      label: 'Team attendance',
      to: '/team/attendance',
      icon: UsersRound,
      requiredPermissions: ['team.read'],
      requiresReports: true,
    },
    {
      // Everyone's requests and balances, adjustments, the balances report (FR-LEAVE-3/8).
      id: 'leave-register',
      label: 'Leave register',
      to: '/hr/leave',
      icon: ClipboardList,
      requiredPermissions: ['leave.read.all'],
    },
    {
      // The attendance register, monthly summary export and WFH calendar (FR-ATT-11/12, FR-WFH-5).
      id: 'attendance',
      label: 'Attendance',
      to: '/hr/attendance',
      icon: Laptop,
      requiredPermissions: ['attendance.read.all'],
    },
    {
      // Flagged punches to accept or void, and HR punch corrections (FR-ATT-10/11).
      id: 'flags',
      label: 'Flagged punches',
      to: '/hr/attendance/flags',
      icon: Flag,
      requiredPermissions: ['attendance.manage'],
    },
    {
      // Office locations for the check-in geofence (FR-ATT-2, FR-ATT-4).
      id: 'offices',
      label: 'Offices',
      to: '/hr/attendance/offices',
      icon: Building2,
      requiredPermissions: ['attendance.manage'],
    },
    {
      // Biometric devices, user-ID exceptions and unmapped punches (FR-ATT-5/6).
      id: 'devices',
      label: 'Devices',
      to: '/hr/attendance/devices',
      icon: Fingerprint,
      requiredPermissions: ['attendance.manage'],
    },
    {
      // Work calendar (FR-CAL-2): HR maintains holidays; everyone reads them through the API.
      id: 'holidays',
      label: 'Holidays',
      to: '/hr/holidays',
      icon: CalendarDays,
      requiredPermissions: ['calendar.manage'],
    },
    {
      // Settings › Leave policy (FR-LEAVE-1, OQ-17): leave types and opening balances.
      id: 'leave-policy',
      label: 'Leave policy',
      to: '/hr/leave-policy',
      icon: ScrollText,
      requiredPermissions: ['leave.manage'],
    },
  ],
  widgets: [
    {
      // Check in / out from the dashboard (FR-ATT-3, PRD US-A1/A2).
      id: 'attendance-today',
      title: 'Attendance today',
      component: AttendanceTodayWidget,
      requiredPermissions: ['attendance.checkin'],
    },
    {
      // Managers: where the team is today (T-07E, FR-ATT-11).
      id: 'team-today',
      title: 'Team today',
      component: TeamTodayWidget,
      requiredPermissions: ['team.read'],
      requiresReports: true,
    },
    {
      // HR: company attendance today and what waits for review (T-07E, FR-ATT-11).
      id: 'attendance-company',
      title: 'Company attendance today',
      component: AttendanceTodayHrWidget,
      requiredPermissions: ['attendance.read.all'],
    },
    {
      id: 'leave-balance',
      title: 'My leave',
      component: LeaveBalanceWidget,
      requiredPermissions: ['leave.request'],
    },
  ],
};
